import * as React from 'react';
import styled from 'styled-components';
import { launchToastError } from '../../containers_v2/forms/visual-form-editor/visualFormEditor';
import { FlexDiv } from '../../containers_v2/products/style';
import { DefaultText } from '../../styles/global/css/GlobalText';
import { BorderColor, DarkGreySidely2, LightGreySidely, PastelPrimaryColor, PrimaryColor, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import Add from '../add/Add';

const PanelSwap = styled.div<{ activeIndex: number, index: number, minWidth?: string, square?: boolean, borderBottom?: boolean, backgroundColor?: string, fontColor?: string, pill?: boolean }>`
	${DefaultText};
	flex-shrink: 0;
	background-color: ${({ backgroundColor }) => backgroundColor ?? SurfaceColor};
	${({ fontColor }) => fontColor ? `color: ${fontColor};` : ''}
	font-weight: 500;
	padding: 5px 20px;
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
	font-size: 12px;
	position: relative;
	width: fit-content;
	${({ minWidth }) => minWidth ? `min-width: ${minWidth};` : ''}
	${({ activeIndex, index, square, borderBottom, pill }) => {
		if (pill) return `
			border-radius: 6px;
			padding: 6px 12px;
			font-size: 13px;
			${activeIndex === index ? `
				background-color: ${PastelPrimaryColor};
				color: ${PrimaryColor};
			` : `
				color: ${DarkGreySidely2};
				cursor: pointer;
				&:hover {
					background-color: ${LightGreySidely};
				}
			`}
		`;
		if (activeIndex === index) return `
			${!square ? `border: 1px solid ${BorderColor}; border-radius: 10px 10px 0 0;` : ''}
			border-bottom: none;
		`;
		return `
			${!square ? 'border: 1px solid transparent;' : ''}
			${!square && borderBottom ? `border-bottom: 1px solid ${BorderColor};` : ''}
			* {
				opacity: 0.5;
			}
			cursor: pointer;
			&:hover > * {
				opacity: 0.75;
			}
		`;
	}}
`;

const PanelToolTip = styled.div<{ left: number, top: number }>`
	position: absolute;
	padding: 0.5em;
	background-color: ${SidelyBlack}CC;
	left: ${p => p.left}px;
	top: calc(${p => p.top}px + 10px);
	z-index: 9;
	color: white;
	font-size: 11px;
	white-space: nowrap;
	&:after {
		content: " ";
		position: absolute;
		bottom: 100%;
		left: 50%;
		margin-left: -5px;
		border-width: 5px;
		border-style: solid;
		border-color: transparent transparent ${SidelyBlack}CC transparent;
	  }
`;

type PanelSelectorProps<T> = {
	panels: T[],
	onClick: (panel: T, index: number) => void,
	activeIndex: number,
	minWidth?: string,
	backgroundColor?: string,
	fontColor?: string,
}

export function PanelSelector<T extends { label: React.ReactNode, img?: string, imgJsx?: JSX.Element, hidden?: boolean, toolTip?: string }>(props: PanelSelectorProps<T> & {
	borderBottom?: boolean,
	noToolTip?: boolean,
	square?: boolean,
	onAddPanel?: () => void,
	imgOnTheRight?: boolean,
	imgOnlyOnActive?: boolean,
	disableAddPanel?: boolean,
	disableAddPanelMsg?: string,
	width?: string,
	pill?: boolean,
}): JSX.Element {
	const [hovered, setHovered] = React.useState<number>();
	const ref = React.useRef<HTMLDivElement>();
	const setRef = React.useCallback(node => ref.current = node, []);
	const panelsSwap = document.querySelectorAll('.panel-swap');
	let founded = false;
	let left = 0;
	panelsSwap.forEach((ps, i) => {
		if (founded) return;
		if (i === hovered) {
			founded = true;
			return;
		}
		left += ps.clientWidth;
	});

	return <>
		<FlexDiv overflow='auto' flexShrink={0} innerRef={setRef} onMouseLeave={() => !props.noToolTip && setHovered(undefined)} width={props.width ?? '100%'} gap={props.pill ? '4px' : undefined} align={props.pill ? 'center' : undefined}>
			{props.panels.map((panel, index) => <PanelSwap
				square={props.square}
				minWidth={props.minWidth}
				hidden={panel.hidden}
				className='panel-swap'
				onMouseEnter={() => !props.noToolTip && setHovered(index)}
				key={`panelSwap[${index}]`}
				activeIndex={props.activeIndex}
				index={index}
				onClick={() => props.onClick(panel, index)}
				borderBottom={props.borderBottom}
				backgroundColor={props.backgroundColor}
				fontColor={props.fontColor}
				pill={props.pill}
			>
				<FlexDiv gap='5px'>
					{props.imgOnTheRight ? panel.label : null}
					{panel.img !== undefined && (!props.imgOnlyOnActive || props.activeIndex === index) &&
						<img height='20px' src={panel.img} />
					}
					{panel.imgJsx !== undefined && (!props.imgOnlyOnActive || props.activeIndex === index) &&
						panel.imgJsx
					}
					{props.imgOnTheRight ? null : panel.label}
				</FlexDiv>
			</PanelSwap>
			)}
			{props.onAddPanel !== undefined &&
				<div style={{ paddingInline: '8px' }}><Add size='20px' onClick={props.disableAddPanel ? props.disableAddPanelMsg ? () => launchToastError(props.disableAddPanelMsg!) : undefined : props.onAddPanel} disabled={props.disableAddPanel} /></div>
			}
			{props.borderBottom && <div style={{ flex: 1, borderBottom: `1px solid ${BorderColor}`, alignSelf: 'end' }} />}
		</FlexDiv>
		{!props.noToolTip && hovered !== undefined && ref.current && <PanelToolTip top={ref.current.offsetTop + ref.current.clientHeight} left={left - ref.current.scrollLeft + ref.current.offsetLeft}>{props.panels[hovered].toolTip ?? props.panels[hovered].label}</PanelToolTip>}
	</>;
}
